import { InstructorTableCellType } from '@/enums/InstructorTableCellType';
import store from '@/store/index';
import { LESSON_TYPE } from "@/constants/LessonTypes";
import { SERVICE_TYPE } from "@/constants/ServiceTypes";
import moment from "moment";
import { NotificationUrgency } from "@/enums/NotificationUrgency";
import { InstructorTableRowType } from "@/enums/InstructorTableRowType";
import _ from "lodash";
import { getPriceForDate, comparePriceToCurrentDate } from "@/utils/PricingResolver";
export default class InstructorTableReservedCell {
    cellType = InstructorTableCellType.RESERVED;
    hour;
    length;
    service; // ski, snb, wex, kid-school, staff, storno
    phone;
    note;
    isPrepaid = false;
    voucher = null;
    voucherBlockedValue = 0;
    clients = [];
    row;
    constructor(row) {
        this.row = row;
    }
    toSaveObject() {
        const { row, ...rest } = _.cloneDeep(this);
        const saveObject = {
            ...rest,
            voucher: this.voucher ? this.voucher.id : null,
        };
        return saveObject;
    }
    get lessonType() {
        return ([SERVICE_TYPE.SNB, SERVICE_TYPE.SKI].includes(this.service) ? LESSON_TYPE.INDIVIDUAL : this.service);
    }
    /**
     * Get the schedule date from the row's table.
     * Works for both InstructorTableInstructorRow and InstructorTableBlockRow.
     */
    get scheduleDate() {
        if (this.row.rowType === InstructorTableRowType.INSTRUCTOR) {
            return this.row.table.date;
        }
        else if (this.row.rowType === InstructorTableRowType.BLOCK) {
            const blockRow = this.row;
            if (blockRow.table) {
                return blockRow.table.date;
            }
        }
        // Fallback to current date if table is not available
        return moment();
    }
    get price() {
        const pricing = getPriceForDate(this.lessonType, this.length, this.clients.length, this.scheduleDate);
        return pricing.data.price;
    }
    get priceIncludingDiscount() {
        return this.price - this.voucherBlockedValue;
    }
    /**
     * Compare the historical price (at schedule date) to the current price.
     * Returns null if the schedule date is today or in the future.
     */
    get priceComparison() {
        // Only show comparison for past dates
        if (!this.scheduleDate.isBefore(moment(), 'day')) {
            return null;
        }
        return comparePriceToCurrentDate(this.lessonType, this.length, this.clients.length, this.scheduleDate);
    }
    async useVoucher(voucher) {
        if (this.voucher !== null) {
            throw new Error('Na tuto výuku byl již poukaz aplikován!');
        }
        const price = this.price;
        if (voucher.data.remainingValue >= price) {
            await store.dispatch('voucher/use', {
                voucher: voucher,
                value: price,
            });
            this.voucherBlockedValue = price;
        }
        else {
            const value = voucher.data.remainingValue;
            await store.dispatch('voucher/use', {
                voucher: voucher,
                value: value,
            });
            this.voucherBlockedValue = value;
        }
        this.voucher = voucher;
    }
    async clearVoucher() {
        if (this.voucher === null) {
            throw new Error('Žádný voucher nebyl použit!');
        }
        await store.dispatch('voucher/use', {
            voucher: this.voucher,
            value: -this.voucherBlockedValue,
        });
        this.voucher = null;
        this.voucherBlockedValue = 0;
    }
    async notifyInstructor(message, type, link = undefined) {
        if (this.row.rowType !== InstructorTableRowType.INSTRUCTOR
            || this.row.table.date.isBefore(moment(), 'days')) {
            return;
        }
        await store.dispatch('notification/send', {
            instructor: this.row.instructorId,
            message: message,
            type: type,
            urgency: this.notificationUrgency,
            link: link,
        });
    }
    get notificationUrgency() {
        if (moment().add(1, 'days').isAfter(this.row.table.date, 'hours')) {
            return NotificationUrgency.CRITICAL;
        }
        if (moment().add(3, 'days').isAfter(this.row.table.date, 'hours')) {
            return NotificationUrgency.ALERT;
        }
        return NotificationUrgency.INFO;
    }
    get calendarCaption() {
        if (this.clients.length === 1) {
            return `${this.clients[0].name} (${this.clients[0].age})`;
        }
        else if (this.clients.length > 1 && this.clients.length < 5) {
            return `${this.clients.length} klienti`;
        }
        else {
            return `${this.clients.length} klientů`;
        }
    }
}
